import React, { useMemo, useState } from 'react'
import { Button } from '@resiliatech/birdsong'
import { useQuery, useMutation } from '@apollo/client'
import Checkbox from '@mui/material/Checkbox'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import SortIcon from '@mui/icons-material/Sort'
import FilterListIcon from '@mui/icons-material/FilterList'
import EditIcon from '@mui/icons-material/Edit'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DeleteIcon from '@mui/icons-material/Delete'
import GET_ACADEMY_RESOURCES from 'ApolloAcademy/Queries/GetAcademyResources'
import { TabFilterKeyEnum } from 'Components/Pages/Impact/ImpactReportDashboard/types'
import { ACADEMY_CONTEXT } from 'Components/Pages/Academy/_common/utils'
import { useAppState } from 'Context/AppContext/AppContext'
import { useSnackbar } from 'Context/SnackbarContext/SnackbarContext'
import { useShowUpgradeModalDialog } from 'Hooks/useGlobalDialogDispatchers'
import {
  ChecklistItem,
  Goal,
  RecurringChecklistItem,
} from 'Generated/graphql-types'
import {
  ResourceSortOptions,
  ResourceType,
} from 'Generated/academy-graphql-types'
import { ChecklistItemWithResourceInfo, User } from 'TypeDefs/Goal'
import useNonprofitOrgTeam from 'Hooks/useNonprofitOrgTeam/useNonprofitOrgTeam'
import dayjs from 'dayjs'
import DELETE_CHECKLIST_ITEM from 'Apollo/Mutations/DeleteChecklistItem'
import { GOALS } from 'Apollo/Queries/Goals'
import {
  HeaderAndButtonContainer,
  ButtonContainer,
} from '../../Updates/UpdatesTab.styled'
import {
  Container,
  TaskListContainer,
  HeaderRow,
  HeaderCell,
  ScrollableContent,
  TaskRow,
  TaskNameCell,
  StatusCell,
  DateCell,
  OwnerCell,
  ActionsCell,
  UserAvatar,
  UserInfo,
  ActionsButton,
  StatusPill,
  CheckboxContainer,
  ControlsContainer,
  SortButton,
  FilterButton,
  EmptyStateContainer,
  EmptyStateTitle,
  EmptyStateText,
} from './TaskList.styled'
import { TaskListEmptyState } from './TaskListEmptyState'
import { TaskModalV2 } from '../TaskModalV2/TaskModalV2'
import { DeleteTaskModal } from '../../../../Dashboard/NonprofitPlatformDashboard/TaskList/TaskModal/DeleteTaskModal'
import {
  getTasksWithResources,
  getFilteredTasksByTab,
  useTaskUpdate,
  useUpsertRecurringTask,
  buildTaskToastMessage,
} from '../utils'
import { taskAnalytics } from '../analytics.utils'
import { UpsertTaskHandler } from '../types'
// ProgressUpdates is not currently used but may be needed in the future

type TaskListWithTabsProps = {
  goal: Goal
}

type SortOption = 'dueDate' | 'name'
type FilterOption = TabFilterKeyEnum

export const TaskListWithTabs = ({ goal }: TaskListWithTabsProps) => {
  const {
    nonprofitOrgConfig: {
      orgId: nonprofitOrgId,
      platformPlan,
      funderOrgId,
      role: userRole,
    },
    userConfig: { id: userId, email: userEmail },
  } = useAppState()
  const { popToast } = useSnackbar()
  const { teamMap = {} } = useNonprofitOrgTeam()
  const dispatchShowUpgradeModal = useShowUpgradeModalDialog()
  const { upsertRecurringTask, loading: upsertRecurringLoading } =
    useUpsertRecurringTask(goal.id, nonprofitOrgId)
  const { upsertTask, loading: upsertLoading } = useTaskUpdate(
    goal.id,
    nonprofitOrgId
  )

  // Delete task mutation
  const [deleteChecklistItem, { loading: deleteLoading }] = useMutation(
    DELETE_CHECKLIST_ITEM,
    {
      onCompleted: () => {
        popToast({
          content: 'Task deleted successfully',
        })
        // When a task is deleted, also dispatch analytics event
        if (selectedTask) {
          taskAnalytics.dispatchDeleteRecurringTaskDeleted({
            taskId: selectedTask.id || 0,
          })
        }
      },
      onError: (error) => {
        // Silent error handling
        popToast({
          content: `Error deleting task: ${error.message}`,
        })
      },
      refetchQueries: [
        {
          query: GOALS,
          variables: {
            activeOnly: true,
            goalId: goal.id.toString(),
            nonprofitOrgId,
          },
        },
      ],
    }
  )

  const getAssigneeEmail = (user: User | null | undefined) =>
    user?.id ? teamMap[user.id]?.email : null

  const baseEventProps = {
    assignerEmail: userEmail,
    assignerId: userId,
    funderOrgId,
    goalId: goal.id,
    goalType: goal.type,
    isGoalSharedWithFunder: !!goal.FunderOrgToSharedGoal,
    nonprofitOrgId,
    platformPlan,
    templateLibraryId: goal.templateLibraryId || null,
    userId,
    userRole,
  }

  const [selectedTask, setSelectedTask] =
    useState<ChecklistItemWithResourceInfo>()
  const [isDuplicating, setIsDuplicating] = useState(false)
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false)
  // These state variables are currently unused but will be needed for future implementation
  // of progress updates functionality
  // const [activeTask, setActiveTask] = useState<Pick<ChecklistItemWithResourceInfo, 'id'>>()
  // const [showProgressUpdates, setShowProgressUpdates] = useState(false)

  // Sort and filter states
  const [sortBy, setSortBy] = useState<SortOption>('dueDate')
  const [filterBy, setFilterBy] = useState<FilterOption>(TabFilterKeyEnum.all)
  const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null)
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null)
  const [taskMenuAnchorEl, setTaskMenuAnchorEl] = useState<null | HTMLElement>(
    null
  )
  const [taskForMenu, setTaskForMenu] =
    useState<ChecklistItemWithResourceInfo | null>(null)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)

  const handleSortClick = (event: React.MouseEvent<HTMLElement>) => {
    setSortAnchorEl(event.currentTarget)
  }

  const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setFilterAnchorEl(event.currentTarget)
  }

  const handleSortClose = () => {
    setSortAnchorEl(null)
  }

  const handleFilterClose = () => {
    setFilterAnchorEl(null)
  }

  const handleSortSelect = (option: SortOption) => {
    setSortBy(option)
    handleSortClose()
  }

  const handleFilterSelect = (option: FilterOption) => {
    setFilterBy(option)
    handleFilterClose()
  }

  const handleTaskMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    task: ChecklistItemWithResourceInfo
  ) => {
    setTaskMenuAnchorEl(event.currentTarget)
    setTaskForMenu(task)
  }

  const handleTaskMenuClose = () => {
    setTaskMenuAnchorEl(null)
    setTaskForMenu(null)
  }

  const handleResetTaskState = () => {
    setIsTaskModalOpen(false)
    setIsDuplicating(false)
    setSelectedTask(undefined)
  }

  const handleSelectTask = (task: ChecklistItemWithResourceInfo) => {
    const isRecurring = !!task.checklistItemGroupId
    if (isRecurring) {
      taskAnalytics.dispatchEditRecurringTaskClicked({
        ...baseEventProps,
        assigneeUserEmail: getAssigneeEmail(task.owner) || '',
        dueDate: task.dueDate ?? null,
        ownerId: task.owner?.id ?? null,
        taskId: task.id,
        taskUpdatedDate: task.updatedAt,
      })
    } else {
      taskAnalytics.dispatchGoalTaskEditTaskClicked({
        ...baseEventProps,
        assigneeUserEmail: getAssigneeEmail(task.owner) || '',
        dueDate: task.dueDate ?? null,
        ownerId: task.owner?.id ?? null,
        taskId: task.id,
        taskUpdatedDate: task.updatedAt,
      })
    }
    setSelectedTask(task)
    setIsTaskModalOpen(true)
    handleTaskMenuClose()
  }

  const handleDuplicateTask = (task: ChecklistItemWithResourceInfo) => {
    const isRecurring = !!task.checklistItemGroupId
    if (isRecurring) {
      taskAnalytics.dispatchDuplicateRecurringTaskClicked({
        ...baseEventProps,
        assigneeUserEmail: getAssigneeEmail(task.owner) || '',
        dueDate: task.dueDate ?? null,
        ownerId: task.owner?.id ?? null,
        taskId: task.id,
        taskUpdatedDate: task.updatedAt,
      })
    } else {
      taskAnalytics.dispatchGoalTaskDuplicateTaskClicked({
        ...baseEventProps,
        assigneeUserEmail: getAssigneeEmail(task.owner) || '',
        dueDate: task.dueDate ?? null,
        ownerId: task.owner?.id ?? null,
        taskId: task.id,
        taskUpdatedDate: task.updatedAt,
      })
    }
    setSelectedTask(task)
    setIsDuplicating(true)
    setIsTaskModalOpen(true)
    handleTaskMenuClose()
  }

  const handleDeleteTask = (task: ChecklistItemWithResourceInfo) => {
    // Tracking analytics
    const isRecurring = !!task.checklistItemGroupId
    if (isRecurring) {
      taskAnalytics.dispatchDeleteRecurringTaskClicked({
        ...baseEventProps,
        assigneeUserEmail: getAssigneeEmail(task.owner) || '',
        dueDate: task.dueDate ?? null,
        ownerId: task.owner?.id ?? null,
        taskId: task.id,
        taskUpdatedDate: task.updatedAt,
      })
    } else {
      taskAnalytics.dispatchGoalTaskDeleteTaskClicked({
        ...baseEventProps,
        assigneeUserEmail: getAssigneeEmail(task.owner) || '',
        dueDate: task.dueDate ?? null,
        ownerId: task.owner?.id ?? null,
        taskId: task.id,
        taskUpdatedDate: task.updatedAt,
      })
    }

    // Open delete confirmation dialog
    setSelectedTask(task)
    setIsDeleteDialogOpen(true)
    handleTaskMenuClose()
  }

  const handleConfirmDelete = async () => {
    if (!selectedTask) return

    try {
      await deleteChecklistItem({
        variables: {
          id: selectedTask.id,
          nonprofitOrgId,
        },
      })
    } catch (error) {
      // Silent error handling - the error is already handled in the mutation's onError
    } finally {
      setIsDeleteDialogOpen(false)
      setSelectedTask(undefined)
    }
  }

  const handleUpsertTask: UpsertTaskHandler = async (
    taskInfo: Partial<ChecklistItemWithResourceInfo>,
    recurrenceOptions?: RecurringChecklistItem,
    shouldFireCreateEvent = false,
    shouldPopToast = false,
    isRecurring = false
  ) => {
    if (!taskInfo.title) return
    // Progress updates functionality is disabled for now
    // if (taskInfo.isCompleted) {
    //   setActiveTask(taskInfo)
    //   setShowProgressUpdates(true)
    // }

    const updates = taskInfo

    // Ensure resource is not gated by plan
    if ('resource' in taskInfo && taskInfo.resource?.gatedByPlan) {
      dispatchShowUpgradeModal(true)
      return
    }

    // Ensure URL is fully qualified
    if ('linkUrl' in taskInfo) {
      const { linkUrl } = taskInfo
      if (linkUrl && !linkUrl.includes('http'))
        updates.linkUrl = `https://${linkUrl}`
    }

    // Clear the ID, group ID, and completion values when duplicating tasks
    const duplicatingModifiers = isDuplicating
      ? {
          id: undefined,
          checklistItemGroupId: null,
          isCompleted: false,
          completedAt: null,
        }
      : {}

    const taskInfoToSave = {
      ...selectedTask,
      ...updates,
      ...duplicatingModifiers,
    }

    const toastMessage = buildTaskToastMessage({
      isRecurring,
      isDuplicating,
      selectedTask,
    })

    if (isRecurring && recurrenceOptions) {
      const recurringTasks = await upsertRecurringTask(
        taskInfoToSave,
        recurrenceOptions
      )
      recurringTasks.forEach((task: Partial<ChecklistItem>) => {
        if (shouldFireCreateEvent) {
          taskAnalytics.dispatchGoalTaskCreated({
            ...baseEventProps,
            assigneeUserEmail: getAssigneeEmail(task.owner),
            dueDate: task.dueDate,
            ownerId: task.owner?.id,
            taskDescription: task.title ?? '',
            taskId: task.id,
            taskUpdatedDate: task.updatedAt,
            dayOfWeek: recurrenceOptions.dayOfWeek ?? undefined,
            dayOfMonth: recurrenceOptions.dayOfMonth ?? undefined,
            endDate: recurrenceOptions.endDate ?? undefined,
            frequency: recurrenceOptions.frequency,
            recurrenceType: recurrenceOptions.recurrenceType,
            startDate: recurrenceOptions.startDate,
            weekOfMonth: recurrenceOptions.weekOfMonth ?? undefined,
          })
        }
      })
    } else {
      const upsertedTask = await upsertTask(taskInfoToSave)
      if (selectedTask) {
        taskAnalytics.dispatchGoalTaskEdited({
          ...baseEventProps,
          assigneeUserEmail: getAssigneeEmail(selectedTask.owner),
          dueDate: upsertedTask.dueDate,
          ownerId: upsertedTask.owner?.id,
          taskDescription: upsertedTask.title,
          taskId: selectedTask.id,
          taskUpdatedDate: selectedTask.updatedAt,
        })
      } else if (shouldFireCreateEvent) {
        taskAnalytics.dispatchGoalTaskCreated({
          ...baseEventProps,
          assigneeUserEmail: getAssigneeEmail(upsertedTask.owner),
          dueDate: upsertedTask.dueDate,
          ownerId: upsertedTask.owner?.id,
          taskDescription: upsertedTask.title,
          taskId: upsertedTask.id,
          taskUpdatedDate: upsertedTask.updatedAt,
        })
      }
    }

    taskAnalytics.dispatchGoalTaskSaveClicked(
      taskInfoToSave as ChecklistItemWithResourceInfo,
      baseEventProps
    )

    if (shouldPopToast) popToast({ content: toastMessage })
    handleResetTaskState()
  }

  const handleAddTask = () => {
    taskAnalytics.dispatchGoalAddTaskClicked(baseEventProps)
    setSelectedTask(undefined)
    setIsTaskModalOpen(true)
  }

  const tasks = useMemo(() => goal.ChecklistItem || [], [goal.ChecklistItem])

  const getResourceIdsForItems = useMemo(
    () =>
      tasks
        .map((checklistItem: ChecklistItem) => checklistItem.resourceId)
        .filter((resourceId): resourceId is string => resourceId !== null),
    [tasks]
  )

  const { data: resourceData } = useQuery(GET_ACADEMY_RESOURCES, {
    ...ACADEMY_CONTEXT,
    variables: {
      ids: getResourceIdsForItems,
      resourceType: [
        ResourceType.Article,
        ResourceType.Course,
        ResourceType.Template,
        ResourceType.LearningTrack,
      ],
      query: '',
      sort: ResourceSortOptions.Random,
      anonymousId: global.analytics?.user?.()?.anonymousId?.(),
    },
    skip: getResourceIdsForItems.length === 0,
  })

  const tasksWithResources = useMemo(
    () =>
      getTasksWithResources({
        tasks,
        resources: resourceData?.resources?.nodes || [],
      }),
    [tasks, resourceData]
  )

  const {
    assignedToMeTasks,
    completedTasks,
    pastDueTasks,
    todo,
    unassignedTasks,
  } = getFilteredTasksByTab(tasksWithResources, userId)

  const tabToTasksMap: Record<FilterOption, ChecklistItemWithResourceInfo[]> = {
    [TabFilterKeyEnum.all]: tasksWithResources,
    [TabFilterKeyEnum.assignedToMe]: assignedToMeTasks,
    [TabFilterKeyEnum.completed]: completedTasks,
    [TabFilterKeyEnum.pastDue]: pastDueTasks,
    [TabFilterKeyEnum.todo]: todo,
    [TabFilterKeyEnum.unassigned]: unassignedTasks,
  }

  // Get the filtered tasks based on the selected filter
  const filteredTasks = tabToTasksMap[filterBy]

  // Apply sorting to the filtered tasks
  const sortedTasks = [...filteredTasks].sort((a, b) => {
    if (sortBy === 'dueDate') {
      const dateA = a.dueDate
        ? new Date(a.dueDate).getTime()
        : Number.MAX_SAFE_INTEGER
      const dateB = b.dueDate
        ? new Date(b.dueDate).getTime()
        : Number.MAX_SAFE_INTEGER
      return dateA - dateB
    }
    // Sort by name
    return (a.title || '').localeCompare(b.title || '')
  })

  // Helper function to get user initials
  const getUserInitials = (
    firstName: string | null | undefined,
    lastName: string | null | undefined
  ) => {
    const firstInitial = firstName?.charAt(0) || ''
    const lastInitial = lastName?.charAt(0) || ''
    return `${firstInitial}${lastInitial}`.toUpperCase()
  }

  // Helper function to render task status
  const getTaskStatus = (task: ChecklistItemWithResourceInfo) => {
    if (task.isCompleted) {
      return 'completed'
    }
    // Logic to determine if task is in progress
    return 'in progress'
  }

  // Format date for display
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'No due date'
    return dayjs(dateString).format('MM/DD/YYYY')
  }

  const handleTaskCheckboxChange = (task: ChecklistItemWithResourceInfo) => {
    // Create a new task object with isCompleted toggled
    const updatedTask = {
      ...task,
      isCompleted: !task.isCompleted,
      completedAt: !task.isCompleted ? new Date().toISOString() : null,
    }

    // Call the upsert function
    handleUpsertTask(updatedTask, undefined, false, false)
  }

  // Check if there are any tasks at all
  const hasNoTasks = tasks.length === 0
  // Check if there are no tasks matching the current filter
  const hasFilteredButNoResults =
    !hasNoTasks && (!sortedTasks || sortedTasks.length === 0)

  return (
    <>
      {isTaskModalOpen && (
        <TaskModalV2
          simple={false} /* Force using the centered modal */
          goal={goal}
          isDuplicating={isDuplicating}
          isLoading={upsertLoading || upsertRecurringLoading}
          isTaskModalOpen={isTaskModalOpen}
          onCloseModal={handleResetTaskState}
          onUpsertTask={handleUpsertTask}
          task={selectedTask}
        />
      )}
      <Container>
        <HeaderAndButtonContainer>
          <ControlsContainer>
            <SortButton onClick={handleSortClick}>
              <SortIcon fontSize='small' />
              Sort
            </SortButton>
            <Menu
              anchorEl={sortAnchorEl}
              open={Boolean(sortAnchorEl)}
              onClose={handleSortClose}
            >
              <MenuItem
                selected={sortBy === 'dueDate'}
                onClick={() => handleSortSelect('dueDate')}
              >
                Due date
              </MenuItem>
              <MenuItem
                selected={sortBy === 'name'}
                onClick={() => handleSortSelect('name')}
              >
                Task name
              </MenuItem>
            </Menu>

            <FilterButton onClick={handleFilterClick}>
              <FilterListIcon fontSize='small' />
              Filters
            </FilterButton>
            <Menu
              anchorEl={filterAnchorEl}
              open={Boolean(filterAnchorEl)}
              onClose={handleFilterClose}
            >
              <MenuItem
                selected={filterBy === TabFilterKeyEnum.all}
                onClick={() => handleFilterSelect(TabFilterKeyEnum.all)}
              >
                All tasks
              </MenuItem>
              <MenuItem
                selected={filterBy === TabFilterKeyEnum.todo}
                onClick={() => handleFilterSelect(TabFilterKeyEnum.todo)}
              >
                To Do
              </MenuItem>
              <MenuItem
                selected={filterBy === TabFilterKeyEnum.completed}
                onClick={() => handleFilterSelect(TabFilterKeyEnum.completed)}
              >
                Completed
              </MenuItem>
              <MenuItem
                selected={filterBy === TabFilterKeyEnum.pastDue}
                onClick={() => handleFilterSelect(TabFilterKeyEnum.pastDue)}
              >
                Past Due
              </MenuItem>
              <MenuItem
                selected={filterBy === TabFilterKeyEnum.assignedToMe}
                onClick={() =>
                  handleFilterSelect(TabFilterKeyEnum.assignedToMe)
                }
              >
                Assigned to Me
              </MenuItem>
              <MenuItem
                selected={filterBy === TabFilterKeyEnum.unassigned}
                onClick={() => handleFilterSelect(TabFilterKeyEnum.unassigned)}
              >
                Unassigned
              </MenuItem>
            </Menu>
          </ControlsContainer>

          <ButtonContainer>
            <Button variant='table' label='Add Task' onClick={handleAddTask} />
          </ButtonContainer>
        </HeaderAndButtonContainer>

        <TaskListContainer>
          {/* Render appropriate content based on task state */}
          {(() => {
            // Case 1: No tasks at all
            if (hasNoTasks) {
              return <TaskListEmptyState onAddTask={handleAddTask} />
            }

            // Case 2: Has tasks but none match the current filter
            if (hasFilteredButNoResults) {
              return (
                <EmptyStateContainer>
                  <EmptyStateTitle>No Tasks Found</EmptyStateTitle>
                  <EmptyStateText>
                    There are no tasks matching your current filter. Try
                    changing your filter or add a new task.
                  </EmptyStateText>
                </EmptyStateContainer>
              )
            }

            // Case 3: Has tasks that match the filter
            return (
              <>
                <HeaderRow>
                  <HeaderCell /> {/* Checkbox column */}
                  <HeaderCell>Task</HeaderCell>
                  <HeaderCell>Status</HeaderCell>
                  <HeaderCell>Due Date</HeaderCell>
                  <HeaderCell>Owner</HeaderCell>
                  <HeaderCell>Actions</HeaderCell>
                </HeaderRow>
                <ScrollableContent>
                  {sortedTasks.map((task) => (
                    <TaskRow key={task.id}>
                      <CheckboxContainer>
                        <Checkbox
                          checked={!!task.isCompleted}
                          onChange={() => handleTaskCheckboxChange(task)}
                          size='small'
                        />
                      </CheckboxContainer>
                      <TaskNameCell>{task.title}</TaskNameCell>
                      <StatusCell>
                        <StatusPill status={getTaskStatus(task)}>
                          {task.isCompleted ? 'Completed' : 'In Progress'}
                        </StatusPill>
                      </StatusCell>
                      <DateCell>{formatDate(task.dueDate)}</DateCell>
                      <OwnerCell>
                        {task.owner ? (
                          <>
                            <UserAvatar>
                              {getUserInitials(
                                task.owner.firstName,
                                task.owner.lastName
                              )}
                            </UserAvatar>
                            <UserInfo>
                              {task.owner.firstName} {task.owner.lastName}
                            </UserInfo>
                          </>
                        ) : (
                          <UserInfo>Unassigned</UserInfo>
                        )}
                      </OwnerCell>
                      <ActionsCell>
                        <ActionsButton
                          onClick={(e) => handleTaskMenuOpen(e, task)}
                        >
                          <MoreHorizIcon fontSize='small' />
                        </ActionsButton>
                      </ActionsCell>
                    </TaskRow>
                  ))}
                </ScrollableContent>
              </>
            )
          })()}

          {/* Task actions menu */}
          <Menu
            anchorEl={taskMenuAnchorEl}
            open={Boolean(taskMenuAnchorEl)}
            onClose={handleTaskMenuClose}
            PaperProps={{
              style: {
                width: '180px',
              },
            }}
          >
            <MenuItem
              onClick={() => taskForMenu && handleSelectTask(taskForMenu)}
              sx={{ gap: '12px', py: '10px' }}
            >
              <EditIcon fontSize='small' />
              Edit
            </MenuItem>
            <MenuItem
              onClick={() => taskForMenu && handleDuplicateTask(taskForMenu)}
              sx={{ gap: '12px', py: '10px' }}
            >
              <ContentCopyIcon fontSize='small' />
              Duplicate
            </MenuItem>
            <MenuItem
              onClick={() => taskForMenu && handleDeleteTask(taskForMenu)}
              sx={{ gap: '12px', py: '10px' }}
            >
              <DeleteIcon fontSize='small' />
              Delete
            </MenuItem>
          </Menu>
        </TaskListContainer>
      </Container>

      {/* Delete confirmation dialog */}
      <DeleteTaskModal
        isOpen={isDeleteDialogOpen}
        onClose={() => {
          setIsDeleteDialogOpen(false)
          setSelectedTask(undefined)
        }}
        onDeleteTask={async (taskId) => {
          await handleConfirmDelete()
        }}
        taskId={selectedTask?.id || 0}
      />
    </>
  )
}
